// İlk önce CSS import
import '../../../context/AuthContext.css';

// Sonra React ve diğer kütüphane importları
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import Ga4WebOnlyNote from '../../../components/Ga4WebOnlyNote';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';
import { formatDateYMD } from '../../../utils/dateRangeHelpers';
import { useAccounts } from '../../../context/AccountContext';
import { Card } from '../../../components/ui/Card';
import {
  Filter,
  Download,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  LayoutList,
  TrendingUp,
  DollarSign,
  MousePointerClick,
  ShoppingCart,
  Check,
  Calendar,
  Loader2,
  Edit2,
  X,
  AlertTriangle,
  Activity,
  History,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
// DatePicker importlarını ekleyelim
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Sample ad groups data structure
const sampleAdGroups = {
  'search-1': [
    {
      id: 'ag1',
      name: 'Brand Keywords',
      status: 'Active',
      spend: 5200,
      impressions: 18000,
      clicks: 1200,
      ctr: 6.67,
      cpc: 4.33,
      conversions: 85,
      conversionRate: 7.08,
      costPerConversion: 61.18,
      revenue: 22100,
      roas: 4.25,
      keywords: [
        { id: 'kw1-1', text: 'glowytics brand', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 4200, clicks: 320, ctr: 7.62, cpc: 4.10, conversions: 22, conversionRate: 6.88, costPerConversion: 59.64, revenue: 5800, roas: 4.42 },
        { id: 'kw1-2', text: 'glowytics analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3800, clicks: 280, ctr: 7.37, cpc: 4.25, conversions: 19, conversionRate: 6.79, costPerConversion: 62.63, revenue: 4900, roas: 4.12 },
        { id: 'kw1-3', text: 'glowytics platform', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 4.40, conversions: 16, conversionRate: 6.96, costPerConversion: 63.25, revenue: 4200, roas: 4.15 },
        { id: 'kw1-4', text: 'glowytics software', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3300, clicks: 210, ctr: 6.36, cpc: 4.50, conversions: 15, conversionRate: 7.14, costPerConversion: 63.00, revenue: 3800, roas: 4.02 },
        { id: 'kw1-5', text: 'glowytics marketing', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3200, clicks: 160, ctr: 5.00, cpc: 4.60, conversions: 13, conversionRate: 8.13, costPerConversion: 56.62, revenue: 3400, roas: 4.60 }
      ]
    },
    {
      id: 'ag2',
      name: 'Generic Keywords',
      status: 'Active',
      spend: 4800,
      impressions: 16500,
      clicks: 1100,
      ctr: 6.67,
      cpc: 4.36,
      conversions: 65,
      conversionRate: 5.91,
      costPerConversion: 73.85,
      revenue: 18200,
      roas: 3.79,
      keywords: [
        { id: 'kw2-1', text: 'marketing analytics', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3800, clicks: 250, ctr: 6.58, cpc: 4.20, conversions: 14, conversionRate: 5.60, costPerConversion: 75.00, revenue: 3900, roas: 3.71 },
        { id: 'kw2-2', text: 'analytics platform', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 4.30, conversions: 13, conversionRate: 5.65, costPerConversion: 76.15, revenue: 3700, roas: 3.75 },
        { id: 'kw2-3', text: 'marketing dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3200, clicks: 220, ctr: 6.88, cpc: 4.40, conversions: 13, conversionRate: 5.91, costPerConversion: 74.46, revenue: 3600, roas: 3.72 },
        { id: 'kw2-4', text: 'ads performance tracking', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3100, clicks: 210, ctr: 6.77, cpc: 4.50, conversions: 13, conversionRate: 6.19, costPerConversion: 72.69, revenue: 3500, roas: 3.70 },
        { id: 'kw2-5', text: 'marketing roi tool', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2900, clicks: 190, ctr: 6.55, cpc: 4.60, conversions: 12, conversionRate: 6.32, costPerConversion: 72.83, revenue: 3500, roas: 4.01 }
      ]
    },
    {
      id: 'ag3',
      name: 'Competitor Keywords',
      status: 'Active',
      spend: 2500,
      impressions: 10500,
      clicks: 900,
      ctr: 8.57,
      cpc: 2.78,
      conversions: 30,
      conversionRate: 3.33,
      costPerConversion: 83.33,
      revenue: 12200,
      roas: 4.88,
      keywords: [
        { id: 'kw3-1', text: 'google analytics alternative', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2400, clicks: 200, ctr: 8.33, cpc: 2.70, conversions: 7, conversionRate: 3.50, costPerConversion: 77.14, revenue: 2600, roas: 4.85 },
        { id: 'kw3-2', text: 'better than semrush', status: 'Active', matchType: 'Exact', qualityScore: 6, impressions: 2200, clicks: 190, ctr: 8.64, cpc: 2.75, conversions: 6, conversionRate: 3.16, costPerConversion: 87.08, revenue: 2500, roas: 4.79 },
        { id: 'kw3-3', text: 'hubspot vs', status: 'Active', matchType: 'Broad', qualityScore: 5, impressions: 2100, clicks: 180, ctr: 8.57, cpc: 2.80, conversions: 6, conversionRate: 3.33, costPerConversion: 84.00, revenue: 2400, roas: 4.76 },
        { id: 'kw3-4', text: 'mixpanel alternative', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2000, clicks: 170, ctr: 8.50, cpc: 2.85, conversions: 6, conversionRate: 3.53, costPerConversion: 80.75, revenue: 2400, roas: 4.96 },
        { id: 'kw3-5', text: 'amplitude vs', status: 'Active', matchType: 'Broad', qualityScore: 5, impressions: 1800, clicks: 160, ctr: 8.89, cpc: 2.80, conversions: 5, conversionRate: 3.13, costPerConversion: 89.60, revenue: 2300, roas: 5.11 }
      ]
    }
  ],
  'search-2': [
    {
      id: 'ag4',
      name: 'Product Keywords',
      status: 'Active',
      spend: 3800,
      impressions: 14200,
      clicks: 980,
      ctr: 6.90,
      cpc: 3.88,
      conversions: 52,
      conversionRate: 5.31,
      costPerConversion: 73.08,
      revenue: 15600,
      roas: 4.11,
      keywords: [
        { id: 'kw4-1', text: 'analytics dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3200, clicks: 220, ctr: 6.88, cpc: 3.80, conversions: 12, conversionRate: 5.45, costPerConversion: 69.67, revenue: 3500, roas: 4.18 },
        { id: 'kw4-2', text: 'marketing analytics tool', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3000, clicks: 210, ctr: 7.00, cpc: 3.85, conversions: 11, conversionRate: 5.24, costPerConversion: 73.55, revenue: 3300, roas: 4.08 },
        { id: 'kw4-3', text: 'ad performance tracker', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2800, clicks: 190, ctr: 6.79, cpc: 3.90, conversions: 10, conversionRate: 5.26, costPerConversion: 74.10, revenue: 3000, roas: 4.05 },
        { id: 'kw4-4', text: 'campaign analytics software', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2700, clicks: 180, ctr: 6.67, cpc: 3.95, conversions: 10, conversionRate: 5.56, costPerConversion: 71.10, revenue: 2900, roas: 4.08 },
        { id: 'kw4-5', text: 'marketing performance dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 180, ctr: 7.20, cpc: 3.90, conversions: 9, conversionRate: 5.00, costPerConversion: 78.00, revenue: 2900, roas: 4.14 }
      ]
    },
    {
      id: 'ag5',
      name: 'Category Keywords',
      status: 'Active',
      spend: 4200,
      impressions: 15800,
      clicks: 1050,
      ctr: 6.65,
      cpc: 4.00,
      conversions: 58,
      conversionRate: 5.52,
      costPerConversion: 72.41,
      revenue: 17400,
      roas: 4.14,
      keywords: [
        { id: 'kw5-1', text: 'marketing analytics', status: 'Active', matchType: 'Broad', qualityScore: 7, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 3.95, conversions: 13, conversionRate: 5.65, costPerConversion: 69.92, revenue: 3800, roas: 4.18 },
        { id: 'kw5-2', text: 'digital marketing tools', status: 'Active', matchType: 'Phrase', qualityScore: 8, impressions: 3300, clicks: 220, ctr: 6.67, cpc: 4.00, conversions: 12, conversionRate: 5.45, costPerConversion: 73.33, revenue: 3600, roas: 4.09 },
        { id: 'kw5-3', text: 'ad campaign management', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3100, clicks: 210, ctr: 6.77, cpc: 4.05, conversions: 12, conversionRate: 5.71, costPerConversion: 70.88, revenue: 3500, roas: 4.12 },
        { id: 'kw5-4', text: 'marketing performance tracking', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3000, clicks: 200, ctr: 6.67, cpc: 4.10, conversions: 11, conversionRate: 5.50, costPerConversion: 74.55, revenue: 3300, roas: 4.02 },
        { id: 'kw5-5', text: 'advertising analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2900, clicks: 190, ctr: 6.55, cpc: 4.15, conversions: 10, conversionRate: 5.26, costPerConversion: 78.85, revenue: 3200, roas: 4.06 }
      ]
    }
  ],
  'search-3': [
    {
      id: 'ag6',
      name: 'Location Keywords',
      status: 'Active',
      spend: 2800,
      impressions: 11200,
      clicks: 780,
      ctr: 6.96,
      cpc: 3.59,
      conversions: 42,
      conversionRate: 5.38,
      costPerConversion: 66.67,
      revenue: 12600,
      roas: 4.50,
      keywords: [
        { id: 'kw6-1', text: 'marketing analytics istanbul', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 170, ctr: 6.80, cpc: 3.55, conversions: 9, conversionRate: 5.29, costPerConversion: 67.06, revenue: 2700, roas: 4.47 },
        { id: 'kw6-2', text: 'ankara digital marketing tools', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2300, clicks: 160, ctr: 6.96, cpc: 3.60, conversions: 9, conversionRate: 5.63, costPerConversion: 64.00, revenue: 2600, roas: 4.51 },
        { id: 'kw6-3', text: 'izmir ad campaign management', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2200, clicks: 150, ctr: 6.82, cpc: 3.65, conversions: 8, conversionRate: 5.33, costPerConversion: 68.44, revenue: 2500, roas: 4.56 },
        { id: 'kw6-4', text: 'antalya marketing analytics', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2100, clicks: 150, ctr: 7.14, cpc: 3.55, conversions: 8, conversionRate: 5.33, costPerConversion: 66.56, revenue: 2400, roas: 4.52 },
        { id: 'kw6-5', text: 'bursa advertising analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2100, clicks: 150, ctr: 7.14, cpc: 3.60, conversions: 8, conversionRate: 5.33, costPerConversion: 67.50, revenue: 2400, roas: 4.44 }
      ]
    }
  ],
  'search-4': [
    {
      id: 'ag7',
      name: 'Seasonal Keywords',
      status: 'Paused',
      spend: 1500,
      impressions: 8200,
      clicks: 420,
      ctr: 5.12,
      cpc: 3.57,
      conversions: 18,
      conversionRate: 4.29,
      costPerConversion: 83.33,
      revenue: 5400,
      roas: 3.60,
      keywords: [
        { id: 'kw7-1', text: 'summer marketing analytics', status: 'Paused', matchType: 'Broad', qualityScore: 6, impressions: 1800, clicks: 90, ctr: 5.00, cpc: 3.55, conversions: 4, conversionRate: 4.44, costPerConversion: 79.88, revenue: 1200, roas: 3.75 },
        { id: 'kw7-2', text: 'holiday season analytics', status: 'Paused', matchType: 'Phrase', qualityScore: 7, impressions: 1700, clicks: 85, ctr: 5.00, cpc: 3.60, conversions: 4, conversionRate: 4.71, costPerConversion: 76.50, revenue: 1100, roas: 3.60 },
        { id: 'kw7-3', text: 'black friday marketing tools', status: 'Paused', matchType: 'Exact', qualityScore: 8, impressions: 1600, clicks: 85, ctr: 5.31, cpc: 3.50, conversions: 4, conversionRate: 4.71, costPerConversion: 74.38, revenue: 1100, roas: 3.69 },
        { id: 'kw7-4', text: 'winter campaign analytics', status: 'Paused', matchType: 'Broad', qualityScore: 6, impressions: 1600, clicks: 80, ctr: 5.00, cpc: 3.60, conversions: 3, conversionRate: 3.75, costPerConversion: 96.00, revenue: 1000, roas: 3.47 },
        { id: 'kw7-5', text: 'seasonal marketing performance', status: 'Paused', matchType: 'Phrase', qualityScore: 7, impressions: 1500, clicks: 80, ctr: 5.33, cpc: 3.55, conversions: 3, conversionRate: 3.75, costPerConversion: 94.67, revenue: 1000, roas: 3.52 }
      ]
    }
  ],
  'search-5': [
    {
      id: 'ag8',
      name: 'Promotion Keywords',
      status: 'Active',
      spend: 3200,
      impressions: 12800,
      clicks: 890,
      ctr: 6.95,
      cpc: 3.60,
      conversions: 48,
      conversionRate: 5.39,
      costPerConversion: 66.67,
      revenue: 14400,
      roas: 4.50,
      keywords: [
        { id: 'kw8-1', text: 'marketing analytics discount', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2800, clicks: 190, ctr: 6.79, cpc: 3.55, conversions: 10, conversionRate: 5.26, costPerConversion: 67.45, revenue: 3100, roas: 4.59 },
        { id: 'kw8-2', text: 'analytics software promotion', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2600, clicks: 180, ctr: 6.92, cpc: 3.60, conversions: 10, conversionRate: 5.56, costPerConversion: 64.80, revenue: 3000, roas: 4.63 },
        { id: 'kw8-3', text: 'special offer marketing tools', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2500, clicks: 175, ctr: 7.00, cpc: 3.65, conversions: 10, conversionRate: 5.71, costPerConversion: 63.88, revenue: 2900, roas: 4.54 },
        { id: 'kw8-4', text: 'marketing dashboard deal', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 175, ctr: 7.00, cpc: 3.55, conversions: 9, conversionRate: 5.14, costPerConversion: 69.06, revenue: 2800, roas: 4.51 },
        { id: 'kw8-5', text: 'analytics platform offer', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2400, clicks: 170, ctr: 7.08, cpc: 3.60, conversions: 9, conversionRate: 5.29, costPerConversion: 68.00, revenue: 2600, roas: 4.25 }
      ]
    }
  ],
  'search-6': [
    {
      id: 'ag9',
      name: 'Long-tail Keywords',
      status: 'Active',
      spend: 2900,
      impressions: 11600,
      clicks: 810,
      ctr: 6.98,
      cpc: 3.58,
      conversions: 44,
      conversionRate: 5.43,
      costPerConversion: 65.91,
      revenue: 13200,
      roas: 4.55,
      keywords: [
        { id: 'kw9-1', text: 'best marketing analytics platform for small business', status: 'Active', matchType: 'Broad', qualityScore: 7, impressions: 2500, clicks: 170, ctr: 6.80, cpc: 3.55, conversions: 9, conversionRate: 5.29, costPerConversion: 67.06, revenue: 2800, roas: 4.64 },
        { id: 'kw9-2', text: 'how to track marketing campaign performance effectively', status: 'Active', matchType: 'Phrase', qualityScore: 6, impressions: 2400, clicks: 165, ctr: 6.88, cpc: 3.60, conversions: 9, conversionRate: 5.45, costPerConversion: 66.00, revenue: 2700, roas: 4.55 },
        { id: 'kw9-3', text: 'affordable marketing analytics software for startups', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2300, clicks: 160, ctr: 6.96, cpc: 3.55, conversions: 9, conversionRate: 5.63, costPerConversion: 63.11, revenue: 2600, roas: 4.59 },
        { id: 'kw9-4', text: 'marketing roi tracking tool for multiple platforms', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2200, clicks: 160, ctr: 7.27, cpc: 3.60, conversions: 9, conversionRate: 5.63, costPerConversion: 64.00, revenue: 2600, roas: 4.51 },
        { id: 'kw9-5', text: 'all in one marketing analytics dashboard with reporting', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2200, clicks: 155, ctr: 7.05, cpc: 3.55, conversions: 8, conversionRate: 5.16, costPerConversion: 68.78, revenue: 2500, roas: 4.54 }
      ]
    }
  ],
  'search-7': [
    {
      id: 'ag10',
      name: 'Dynamic Keywords',
      status: 'Active',
      spend: 3500,
      impressions: 13500,
      clicks: 940,
      ctr: 6.96,
      cpc: 3.72,
      conversions: 51,
      conversionRate: 5.43,
      costPerConversion: 68.63,
      revenue: 15300,
      roas: 4.37,
      keywords: [
        { id: 'kw10-1', text: '{marketing analytics} software', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2900, clicks: 200, ctr: 6.90, cpc: 3.70, conversions: 11, conversionRate: 5.50, costPerConversion: 67.27, revenue: 3300, roas: 4.46 },
        { id: 'kw10-2', text: '{digital marketing} dashboard', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2800, clicks: 195, ctr: 6.96, cpc: 3.75, conversions: 11, conversionRate: 5.64, costPerConversion: 66.48, revenue: 3200, roas: 4.38 },
        { id: 'kw10-3', text: '{campaign performance} tracking', status: 'Active', matchType: 'Dynamic', qualityScore: 6, impressions: 2700, clicks: 185, ctr: 6.85, cpc: 3.70, conversions: 10, conversionRate: 5.41, costPerConversion: 68.45, revenue: 3000, roas: 4.38 },
        { id: 'kw10-4', text: '{advertising analytics} platform', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2600, clicks: 180, ctr: 6.92, cpc: 3.75, conversions: 10, conversionRate: 5.56, costPerConversion: 67.50, revenue: 2900, roas: 4.30 },
        { id: 'kw10-5', text: '{marketing roi} calculator', status: 'Active', matchType: 'Dynamic', qualityScore: 8, impressions: 2500, clicks: 180, ctr: 7.20, cpc: 3.70, conversions: 9, conversionRate: 5.00, costPerConversion: 74.00, revenue: 2900, roas: 4.35 }
      ]
    }
  ]
};

// Sample chart data
const chartData = {
  labels: ['1 Jan', '2 Jan', '3 Jan', '4 Jan', '5 Jan', '6 Jan', '7 Jan'],
  datasets: [
    {
      label: 'Spend',
      data: [300, 450, 320, 500, 350, 200, 400],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    },
    {
      label: 'Revenue',
      data: [500, 300, 400, 600, 450, 350, 550],
      borderColor: 'rgb(16, 185, 129)',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    }
  ]
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },
      ticks: {
        callback: (value) => {
          if (value >= 1000000) {
            return `₺${(value / 1000000).toFixed(1)}M`;
          } else if (value >= 1000) {
            return `₺${(value / 1000).toFixed(1)}K`;
          }
          return `₺${value}`;
        },
        font: {
          size: 11
        }
      }
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      beginAtZero: true,
      grid: {
        drawOnChartArea: false,
      },
      ticks: {
        callback: (value) => {
          if (value >= 1000000) {
            return `${(value / 1000000).toFixed(1)}M`;
          } else if (value >= 1000) {
            return `${(value / 1000).toFixed(1)}K`;
          }
          return value.toString();
        },
        font: {
          size: 11
        }
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        maxTicksLimit: 10,
        font: {
          size: 11
        }
      }
    }
  },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        boxWidth: 12,
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 15,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            if (context.dataset.label === 'Impressions') {
              label += context.parsed.y.toLocaleString('tr-TR');
            } else {
              label += `₺${context.parsed.y.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
            }
          }
          return label;
        }
      }
    }
  },
  interaction: {
    intersect: false,
    mode: 'index',
  }
};

export default function ShoppingCampaigns() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');

  // Live status/budget overlay (fetched from Google Ads API)
  const [liveDataMap, setLiveDataMap] = useState({});
  const [isLoadingLive, setIsLoadingLive] = useState(false);

  // Inline status/budget editing state
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [editBudget, setEditBudget] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Change History state
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [changeHistoryData, setChangeHistoryData] = useState([]);
  const [changeHistoryTotal, setChangeHistoryTotal] = useState(0);
  const [changeHistoryLoading, setChangeHistoryLoading] = useState(false);
  const [changeHistoryFilter, setChangeHistoryFilter] = useState('all');

  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last30');
  const [gaAccountFilter, setGaAccountFilter] = useState('all');
  const [gaAccounts, setGaAccounts] = useState([]);
  const [keywordData, setKeywordData] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState(new Set());
  // Sıralama için state değişkenleri
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  // Chart verisi için state
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  // Add missing state variables
  const [trendData, setTrendData] = useState([]);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);

  // Update the state to include the new filter option
  const [selectedDateRange, setSelectedDateRange] = useState('7days');

  // State kısmına dataLoading ekleyelim
  const [dataLoading, setDataLoading] = useState(true);

  // Custom date picker için state'leri ekleyelim
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [shoppingCampaignsData, setShoppingCampaignsData] = useState(null);

  // Helper to normalize campaign identifiers and fields coming from API
  const getCampaignKey = (c) => c?.id || c?.campaign_id || c?.campaignId || c?.name || c?.campaignName || Math.random().toString(36).slice(2);
  const getCampaignName = (c) => c?.name || c?.campaignName || 'Unknown Campaign';
  const getCampaignStatus = (c) => c?.status || c?.campaign_status || c?.campaignStatus || 'Unknown';
  const getCampaignSpend = (c) => parseFloat(c?.spend ?? c?.cost ?? 0);
  const getCampaignBudget = (c) => parseFloat(c?.budget ?? c?.budgetAmount ?? c?.budget_amount ?? 0);
  const getCampaignImpr = (c) => parseInt(c?.impressions ?? c?.googleAdsImpression ?? c?.google_ads_impression ?? 0, 10);
  const getCampaignClicks = (c) => parseInt(c?.clicks ?? c?.googleAdsClicks ?? c?.google_ads_clicks ?? 0, 10);
  const getCampaignConv = (c) => parseInt(c?.conversions ?? c?.transactions ?? c?.googleAdsTransaction ?? c?.google_ads_transaction ?? 0, 10);
  const getCampaignRevenue = (c) => parseFloat(c?.conversionValue ?? c?.revenue ?? c?.googleAdsRevenue ?? c?.google_ads_revenue ?? 0);
  const getCampaignGa4Conv = (c) => parseInt(c?.ga4Transaction ?? c?.ga4_transaction ?? 0, 10);
  const getCampaignGa4Revenue = (c) => parseFloat(c?.ga4Revenue ?? c?.ga4_revenue ?? 0);
  const getCampaignSessions = (c) => parseInt(c?.sessions ?? 0, 10);

  // Kampanya genişletme/daraltma işlevi
  const toggleCampaign = (campaignId, e) => {
    e.stopPropagation();
    setExpandedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  // Reklam grubu genişletme/daraltma işlevi
  const toggleAdGroup = (adGroupId, e) => {
    e.stopPropagation();
    setExpandedAdGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(adGroupId)) {
        newSet.delete(adGroupId);
      } else {
        newSet.add(adGroupId);
      }
      return newSet;
    });
  };

  // Sıralama işlevi
  const handleSort = (field) => {
    if (sortField === field) {
      // Aynı alan üzerine tıklandığında sıralama yönünü değiştir
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Yeni bir alan seçildiğinde, varsayılan olarak azalan sıralama yap
      setSortField(field);
      setSortDirection(field === 'budget' ? 'desc' : 'asc');
    }
  };

  // Anahtar kelimeleri sırala
  const sortKeywords = (keywords, field, direction) => {
    if (!field) return keywords;

    return [...keywords].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        // Sayısal değerleri güvenli bir şekilde al
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;

        return direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Metin değerleri için sıralama (anahtar kelime metni, durum vb.)
      if (['text', 'status'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Varsayılan olarak budget'a göre azalan sıralama
      return b.budget - a.budget;
    });
  };

  // Function to organize keyword data into campaigns and ad groups
  const organizeKeywordData = (data) => {
    if (!data || data.length === 0) {
      console.log('No keyword data available');
      return { campaigns: [] };
    }

    console.log(`Organizing ${data.length} keyword records`);

    // Kampanya ve reklam grupları için map'ler oluştur
    const campaignMap = new Map();
    const adGroupMap = new Map();
    
    // Anahtar kelimeleri birleştirmek için bir map oluştur
    const keywordMap = new Map();

    // Önce tüm kampanya ve reklam gruplarını oluştur
    data.forEach(item => {
      const campaignId = item.campaign_id;
      const adGroupId = item.ad_group_id;
      const keywordId = item.keyword_id;
      const keywordText = item.keyword_text?.toLowerCase() || '';

      // Kampanyayı ekle
      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          id: campaignId,
          name: item.campaign_name || 'Unknown Campaign',
          status: item.campaign_status || 'Unknown',
          budget: parseFloat(item.campaign_budget || 0),
          spend: 0, // Reklam gruplarından hesaplanacak
          impressions: 0, // Reklam gruplarından hesaplanacak
          clicks: 0, // Reklam gruplarından hesaplanacak
          conversions: 0, // Reklam gruplarından hesaplanacak
          conversionValue: 0, // Reklam gruplarından hesaplanacak
          adGroups: [],
          expanded: true
        });
      }

      // Reklam grubunu ekle
      if (!adGroupMap.has(adGroupId)) {
        adGroupMap.set(adGroupId, {
          id: adGroupId,
          name: item.ad_group_name || 'Unknown Ad Group',
          status: item.ad_group_status || 'Unknown',
          campaignId: campaignId,
          spend: 0, // Anahtar kelimelerden hesaplanacak
          impressions: 0, // Anahtar kelimelerden hesaplanacak
          clicks: 0, // Anahtar kelimelerden hesaplanacak
          conversions: 0, // Anahtar kelimelerden hesaplanacak
          conversionValue: 0, // Anahtar kelimelerden hesaplanacak
          keywords: [],
          expanded: false
        });
      }

      // Anahtar kelime için benzersiz bir kimlik oluştur (reklam grubu + anahtar kelime metni)
      const uniqueKeywordId = `${adGroupId}_${keywordText}`;
      
      // Anahtar kelimeyi ekle veya güncelle
      if (!keywordMap.has(uniqueKeywordId)) {
        keywordMap.set(uniqueKeywordId, {
          id: keywordId,
          text: keywordText,
          status: item.keyword_status || 'Unknown',
          adGroupId: adGroupId,
          matchType: item.keyword_match_type || 'Unknown',
          spend: parseFloat(item.cost || 0),
          impressions: parseInt(item.impressions || 0, 10),
          clicks: parseInt(item.clicks || 0, 10),
          conversions: parseInt(item.conversions || 0, 10),
          conversionValue: parseFloat(item.conversion_value || 0),
          matchTypes: [item.keyword_match_type || 'Unknown']
        });
      } else {
        // Mevcut anahtar kelimeyi güncelle (metrikleri topla)
        const existingKeyword = keywordMap.get(uniqueKeywordId);
        existingKeyword.spend += parseFloat(item.cost || 0);
        existingKeyword.impressions += parseInt(item.impressions || 0, 10);
        existingKeyword.clicks += parseInt(item.clicks || 0, 10);
        existingKeyword.conversions += parseInt(item.conversions || 0, 10);
        existingKeyword.conversionValue += parseFloat(item.conversion_value || 0);
        
        // Eşleşme türünü ekle (eğer zaten yoksa)
        if (item.keyword_match_type && !existingKeyword.matchTypes.includes(item.keyword_match_type)) {
          existingKeyword.matchTypes.push(item.keyword_match_type);
        }
      }
    });

    // Anahtar kelimeleri reklam gruplarına ekle ve metrikleri hesapla
    adGroupMap.forEach(adGroup => {
      // Anahtar kelimeleri reklam gruplarına ekle
      const adGroupKeywords = Array.from(keywordMap.values())
        .filter(keyword => keyword.adGroupId === adGroup.id);
      
      adGroup.keywords = adGroupKeywords;
      
      // Anahtar kelimelerden reklam grubu metriklerini hesapla
      adGroupKeywords.forEach(keyword => {
        adGroup.spend += keyword.spend || 0;
        adGroup.impressions += keyword.impressions || 0;
        adGroup.clicks += keyword.clicks || 0;
        adGroup.conversions += keyword.conversions || 0;
        adGroup.conversionValue += keyword.conversionValue || 0;
      });
      
      // CTR, CPC, dönüşüm oranı vb. hesapla
      adGroup.ctr = adGroup.impressions > 0 ? (adGroup.clicks / adGroup.impressions) * 100 : 0;
      adGroup.cpc = adGroup.clicks > 0 ? adGroup.spend / adGroup.clicks : 0;
      adGroup.conversionRate = adGroup.clicks > 0 ? (adGroup.conversions / adGroup.clicks) * 100 : 0;
      adGroup.costPerConversion = adGroup.conversions > 0 ? adGroup.spend / adGroup.conversions : 0;
      adGroup.roas = adGroup.spend > 0 ? adGroup.conversionValue / adGroup.spend : 0;
      
      // Reklam grubunu ilgili kampanyaya ekle
      const campaign = campaignMap.get(adGroup.campaignId);
      if (campaign) {
        campaign.adGroups.push(adGroup);
        
        // Reklam grubu metriklerini kampanya metriklerine ekle
        campaign.spend += adGroup.spend || 0;
        campaign.impressions += adGroup.impressions || 0;
        campaign.clicks += adGroup.clicks || 0;
        campaign.conversions += adGroup.conversions || 0;
        campaign.conversionValue += adGroup.conversionValue || 0;
      }
    });

    // Kampanya metriklerini hesapla
    campaignMap.forEach(campaign => {
      // CTR, CPC, dönüşüm oranı vb. hesapla
      campaign.ctr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions) * 100 : 0;
      campaign.cpc = campaign.clicks > 0 ? campaign.spend / campaign.clicks : 0;
      campaign.conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks) * 100 : 0;
      campaign.costPerConversion = campaign.conversions > 0 ? campaign.spend / campaign.conversions : 0;
      campaign.roas = campaign.spend > 0 ? campaign.conversionValue / campaign.spend : 0;
      
      // Kampanya gelirini conversionValue olarak ayarla
      campaign.revenue = campaign.conversionValue;
    });

    // Kampanyaları bir diziye dönüştür
    const campaigns = Array.from(campaignMap.values());
    
    console.log(`Organized ${campaigns.length} campaigns with ${adGroupMap.size} ad groups and ${keywordMap.size} unique keywords`);
    
    return { campaigns };
  };

  // Calculate summary metrics from campaigns
  const calculateSummaryMetrics = (campaigns) => {
    if (!campaigns || campaigns.length === 0) {
      return {
        cpc: 0,
        conversionRate: 0,
        cost: 0,
        roas: 0
      };
    }

    let totalClicks = 0;
    let totalCost = 0;
    let totalConversions = 0;
    let totalRevenue = 0;

    campaigns.forEach(campaign => {
      totalClicks += campaign.clicks || 0;
      totalCost += campaign.spend || 0;
      totalConversions += campaign.conversions || 0;
      totalRevenue += campaign.revenue || 0;
    });

    const avgCpc = totalClicks > 0 ? totalCost / totalClicks : 0;
    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    const roas = totalCost > 0 ? totalRevenue / totalCost : 0;

    return {
      cpc: avgCpc,
      conversionRate: conversionRate,
      cost: totalCost,
      roas: roas
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true); // Veri yüklenmeye başlarken true yap
        // Shopping campaigns sadece kendi endpoint'ini kullanır
        await fetchShoppingCampaigns();
      } catch (error) {
        console.error('Error:', error);
        console.log('Failed to fetch shopping campaign data'); // toast yerine console.log
      } finally {
        setDataLoading(false); // Veri yükleme bittiğinde false yap
      }
    };

    fetchData();
  }, [dateRange, selectedAccountId, gaAccountFilter]);

  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    fetch('https://api.glowytics.com/api/auth/connections', {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId }
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.connections || []);
        const ga = list.filter(c => c.platform === 'GOOGLE_ADS' && c.status === 'ACTIVE');
        setGaAccounts(ga);
      })
      .catch(() => {});
  }, []);

  // Process keyword data when it changes
  useEffect(() => {
    if (keywordData && Array.isArray(keywordData) && keywordData.length > 0) {
      console.log('Processing keyword data...');
      try {
        const { campaigns: fetchedCampaigns } = organizeKeywordData(keywordData);

        // Update state with fetched data
        setCampaigns(fetchedCampaigns);

        // Başlangıçta tüm kampanyalar kapalı olsun
        setExpandedCampaigns(new Set());

        // Log the organized data
        console.log('Organized campaigns:', fetchedCampaigns);

        // Remove the call to generateTrendDataFromKeywords that's causing errors
        // generateTrendDataFromKeywords();

        setIsLoading(false);
      } catch (error) {
        console.error('Error processing keyword data:', error);
        // Remove the toast error message
        // toast.error('Error processing campaign data');
        setIsLoading(false);
      }
    }
  }, [keywordData]);

  // Function to fetch shopping campaigns data from the database
  const fetchShoppingCampaigns = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        return;
      }

      console.log(`Fetching shopping campaign data for brand ID: ${brandId} with date range: ${dateRange}`);

      // Custom date range için URL parametrelerini ekle
      let apiUrl = `https://api.glowytics.com/api/google-ads/shopping-campaigns?dateRange=${dateRange}`;
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const startDateStr = formatDateYMD(customStartDate);
        const endDateStr = formatDateYMD(customEndDate);
        apiUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
      }
      if (gaAccountFilter !== 'all') apiUrl += `&accountId=${gaAccountFilter}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Shopping Campaigns API Error:', errorData);
        throw new Error(`Failed to fetch shopping campaign data: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      
      console.log('Shopping campaign data fetched successfully from view_google_ads_campaigntype_shopping view:', data);
      
      if (data && data.campaigns && data.campaigns.length > 0) {
        console.log(`Retrieved ${data.campaigns.length} shopping campaigns`);
        console.log('Sample shopping campaign:', data.campaigns[0]);
      } else {
        console.log('No shopping campaigns found');
      }
      
      // Save shopping campaigns data to state
      setShoppingCampaignsData(data);
      
      // Update chart with daily trend data - clear chart if no campaigns or no trend data
      if (data && data.campaigns && data.campaigns.length > 0 && data.dailyTrend && data.dailyTrend.length > 0) {
        updateShoppingCampaignsChart(data.dailyTrend);
      } else {
        // Clear chart if no campaigns or no trend data
        console.log('No shopping campaigns found - clearing chart');
        updateShoppingCampaignsChart([]);
      }
      
          } catch (error) {
        console.error('Error fetching shopping campaign data:', error);
        // Set empty data on error
        setShoppingCampaignsData({
          campaigns: [],
          dailyTrend: [],
          totalMetrics: {
          impressions: 0,
          clicks: 0,
          cost: 0,
          transactions: 0,
          revenue: 0,
          ctr: 0,
          cpc: 0,
          conversionRate: 0,
          costPerTransaction: 0,
          roas: 0
        }
      });
        
        // Clear chart on error
        console.log('Error occurred - clearing chart');
        updateShoppingCampaignsChart([]);
    }
  };


  
  // Update the calculateDateRange function to handle the "This Month" filter
  const calculateDateRange = (selectedRange) => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;

      case 'last7':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'last30':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        console.log(`30 days range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
        break;
      case 'last90':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 90);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'thisMonth':
        // First day of current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        // Current day
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'lastMonth':
        // First day of last month
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        // Last day of last month
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };
  
  // Helper function to filter data by date range
  const filterDataByDateRange = (data, dateRange) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Set time to end of day for end date for inclusive comparison
    endDate.setHours(23, 59, 59, 999);
    
    return data.filter(item => {
      if (!item.date) return false;
      
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };
  
  // New function to group keyword data by date and calculate total cost
  const groupKeywordDataByDate = (keywordData) => {
    console.log("========== STARTING DAILY COST CALCULATION ==========");
    console.log("Input data count:", keywordData.length);
    
    // Log the structure of the first few records to understand the data format
    if (keywordData.length > 0) {
      // Log the keys of the first record to see what fields are available
      console.log("First record keys:", Object.keys(keywordData[0]));
      
      // Log the complete first record for inspection
      console.log("First record:", keywordData[0]);
      
      // Check if cost is in a different field
      if (keywordData[0].cost !== undefined) {
        console.log("Found 'cost' field instead of 'cost_micros'");
      }
      
      // Check for nested fields that might contain cost data
      for (const key in keywordData[0]) {
        const value = keywordData[0][key];
        if (value && typeof value === 'object') {
          console.log(`Checking nested object in field '${key}':`, value);
          
          // Check if this nested object has cost-related fields
          if (value.cost !== undefined || value.cost_micros !== undefined) {
            console.log(`Found cost data in nested field '${key}'`);
          }
        }
      }
    }
    
    // Create a map to store costs by date
    const costByDate = new Map();
    let processedRecords = 0;
    let recordsWithDate = 0;
    let recordsWithCost = 0;
    
    // Process each keyword
    keywordData.forEach(keyword => {
      processedRecords++;
      
      // Check if the keyword has a date field
      if (keyword.date) {
        recordsWithDate++;
        
        // Check for cost field (not cost_micros)
        if (keyword.cost !== undefined) {
          recordsWithCost++;
          const date = keyword.date;
          // Assuming cost is already in the correct format (not micros)
          const cost = parseFloat(keyword.cost);
          
          if (costByDate.has(date)) {
            costByDate.set(date, costByDate.get(date) + cost);
          } else {
            costByDate.set(date, cost);
          }
        }
      }
    });
    
    console.log("Processing stats:");
    console.log("- Total records processed:", processedRecords);
    console.log("- Records with date field:", recordsWithDate);
    console.log("- Records with cost field:", recordsWithCost);
    
    // Convert map to array of objects sorted by date
    const dailyCostArray = Array.from(costByDate.entries())
      .map(([date, cost]) => ({ date, cost }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    console.log("========== DAILY COST DATA (GROUPED BY DATE) ==========");
    console.log(dailyCostArray);
    
    // Also log in table format for better readability
    console.log("========== DAILY COST DATA (TABLE FORMAT) ==========");
    console.table(dailyCostArray);
    
    console.log("Total unique dates:", dailyCostArray.length);
    console.log("Date range:", 
      dailyCostArray.length > 0 ? 
      `${dailyCostArray[0].date} to ${dailyCostArray[dailyCostArray.length-1].date}` : 
      "No data"
    );
    console.log("Total cost:", dailyCostArray.reduce((sum, item) => sum + item.cost, 0).toFixed(2));
    console.log("========== END OF DAILY COST CALCULATION ==========");
    
    // Note: Chart will be updated by shopping campaigns data, not keyword data
    
    return dailyCostArray;
  };
  
  // Günlük trend verileriyle grafik güncelleme fonksiyonu
  const updateShoppingCampaignsChart = (dailyTrendData) => {
    if (!dailyTrendData || dailyTrendData.length === 0) {
      console.log("No daily trend data available - clearing chart");
      // Clear chart by setting empty data
      const emptyChartData = {
        labels: [],
        datasets: [
          {
            label: 'Cost',
            data: [],
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.4,
            yAxisID: 'y'
          },
          {
            label: 'Revenue',
            data: [],
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false,
            tension: 0.4,
            yAxisID: 'y'
          },
          {
            label: 'Impressions',
            data: [],
            borderColor: 'rgb(249, 115, 22)',
            backgroundColor: 'rgba(249, 115, 22, 0.1)',
            fill: false,
            tension: 0.4,
            yAxisID: 'y1'
          }
        ]
      };
      setChartData(emptyChartData);
      console.log("Shopping campaigns chart cleared");
      return;
    }
    
    // Tarihleri formatlayarak etiketleri oluştur
    const labels = dailyTrendData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    });
    
    // Maliyet ve gelir değerlerini çıkar
    const costs = dailyTrendData.map(item => item.cost);
    const revenues = dailyTrendData.map(item => item.revenue);
    const impressions = dailyTrendData.map(item => item.impressions);
    
    // Grafik verilerini güncelle
    const newChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Cost',
          data: costs,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: false,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Revenue',
          data: revenues,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: false,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Impressions',
          data: impressions,
          borderColor: 'rgb(249, 115, 22)',
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          fill: false,
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    };
    
    setChartData(newChartData);
    console.log("Shopping campaigns chart updated with new data", newChartData);
  };


  
  // Performans trend verilerini çekmek için yeni fonksiyon
  const fetchPerformanceTrend = async () => {
    try {
      setIsLoadingTrend(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setIsLoadingTrend(false);
        return;
      }
      
      // Use fetch API instead of axios
      const trendUrl = `https://api.glowytics.com/api/google-ads/performance-trend${gaAccountFilter !== 'all' ? '?accountId=' + gaAccountFilter : ''}`;
      const response = await fetch(trendUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // Don't try to parse JSON if response is not OK
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch performance trend: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("API Performance Trend Response:", data);

      if (data && data.trend) {
        setTrendData(data.trend);
      } else {
        setTrendData(generateEmptyTrendData());
      }
      setIsLoadingTrend(false);
    } catch (error) {
      console.error("Error fetching performance trend:", error);
      setTrendData(generateEmptyTrendData());
      setIsLoadingTrend(false);
      // Don't show error toast
    }
  };

  // Keyword verilerinden trend verilerini oluşturan yardımcı fonksiyon
  const generateTrendDataFromKeywords = () => {
    try {
      if (!keywordData || !Array.isArray(keywordData) || keywordData.length === 0) {
        console.log("No keyword data available for trend generation");
        return;
      }

      // Create a map to store metrics by date
      const metricsByDate = new Map();

      // Process all keywords directly from keywordData
      keywordData.forEach(keyword => {
        if (keyword.date) {
          const date = keyword.date;
          
          if (!metricsByDate.has(date)) {
            metricsByDate.set(date, {
              date,
              impressions: 0,
              clicks: 0,
              cost: 0,
              conversions: 0,
              conversionValue: 0
            });
          }

          const metrics = metricsByDate.get(date);
          metrics.impressions += parseInt(keyword.impressions || 0, 10);
          metrics.clicks += parseInt(keyword.clicks || 0, 10);
          metrics.cost += parseFloat(keyword.cost || 0);
          metrics.conversions += parseInt(keyword.conversions || 0, 10);
          metrics.conversionValue += parseFloat(keyword.conversion_value || 0);
        }
      });

      // Convert to array and sort by date
      const trendData = Array.from(metricsByDate.values())
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      // Calculate derived metrics
      trendData.forEach(day => {
        day.ctr = day.impressions > 0 ? (day.clicks / day.impressions) * 100 : 0;
        day.cpc = day.clicks > 0 ? day.cost / day.clicks : 0;
        day.convRate = day.clicks > 0 ? (day.conversions / day.clicks) * 100 : 0;
        day.cpa = day.conversions > 0 ? day.cost / day.conversions : 0;
        day.roas = day.cost > 0 ? day.conversionValue / day.cost : 0;
      });

      console.log("Generated trend data from keywords:", trendData);
      
      return trendData;
    } catch (error) {
      console.error("Error generating trend data:", error);
      return [];
    }
  };
  
  // Boş trend verisi oluşturan yardımcı fonksiyon
  const generateEmptyTrendData = () => {
    console.log("Generating empty trend data for the last 30 days");
    
    // Son 30 günün tarihlerini oluştur
    const labels = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      // Tarihi gün-ay formatında göster
      labels.push(date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }));
    }
    
    return {
      labels: labels,
      datasets: [
        {
          label: 'Spend',
          data: Array(30).fill(0),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Revenue',
          data: Array(30).fill(0),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        }
      ]
    };
  };

  // Normalize campaign status to 'ENABLED' or 'PAUSED'
  const cleanStatus = (s) => {
    if (!s) return '';
    const cleaned = s.replace('CampaignStatus.', '').trim().toUpperCase();
    if (cleaned === 'ACTIVE' || cleaned === 'ENABLED') return 'ENABLED';
    if (cleaned === 'PAUSED') return 'PAUSED';
    return cleaned;
  };

  // Update campaign status or budget via live Google Ads API
  const updateEntity = useCallback(async (entityType, entityId, field, value, campaignId, oldValue, entityName) => {
    setUpdatingId(campaignId);
    setOpenStatusDropdown(null);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ entityType, entityId, field, value, oldValue, entityName }),
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.details?.error || 'Update failed');
      if (data.success) {
        toast.success('Updated successfully');
        setLiveDataMap(prev => {
          const key = String(campaignId);
          const existing = prev[key] || {};
          if (field === 'status') {
            return { ...prev, [key]: { ...existing, status: value } };
          } else if (field === 'amount_micros') {
            return { ...prev, [key]: { ...existing, budget: parseInt(value) / 1000000 } };
          }
          return prev;
        });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingId(null);
      setEditBudget(null);
    }
  }, [selectedBrand?.id]);

  const handleSaveBudget = useCallback((campaignId, campaignName) => {
    if (!editBudget || editBudget.id !== campaignId) return;
    const newValue = parseFloat(editBudget.value);
    const micros = Math.round(newValue * 1000000);
    if (isNaN(micros) || micros <= 0) {
      toast.error('Invalid budget amount');
      return;
    }
    const live = liveDataMap[String(campaignId)];
    if (!live?.budgetId) {
      toast.error('Budget ID not found for this campaign');
      return;
    }
    const oldBudgetMicros = live.budget ? Math.round(live.budget * 1000000) : null;
    setConfirmDialog({
      title: 'Change Budget',
      message: `Are you sure you want to change the budget to ₺${newValue.toLocaleString('tr-TR')}?`,
      variant: 'budget',
      onConfirm: () => {
        updateEntity('budget', live.budgetId, 'amount_micros', micros, campaignId, oldBudgetMicros ? String(oldBudgetMicros) : null, campaignName || null);
        setConfirmDialog(null);
      }
    });
  }, [editBudget, liveDataMap, updateEntity]);

  // Fetch change history data
  const fetchChangeHistory = useCallback(async (offset = 0, append = false) => {
    if (!selectedBrand?.id) return;
    setChangeHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(offset) });
      if (changeHistoryFilter !== 'all') params.set('action', changeHistoryFilter);
      const response = await fetch(`https://api.glowytics.com/api/labs/ads-change-history?${params}`, {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        setChangeHistoryData(prev => append ? [...prev, ...result.data] : result.data);
        setChangeHistoryTotal(result.total);
      }
    } catch (err) {
      console.error('Failed to fetch change history:', err);
    } finally {
      setChangeHistoryLoading(false);
    }
  }, [selectedBrand?.id, changeHistoryFilter]);

  useEffect(() => {
    if (showChangeHistory) fetchChangeHistory(0);
  }, [showChangeHistory, fetchChangeHistory]);

  // Fetch live status + daily budget from Google Ads API
  const fetchLiveCampaignData = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setIsLoadingLive(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        credentials: 'include'
      });
      const data = await response.json();
      console.log('[ShoppingCampaigns] campaigns-live response:', { ok: response.ok, success: data.success, count: data.campaigns?.length, error: data.error });
      if (response.ok && data.success && Array.isArray(data.campaigns)) {
        const map = {};
        data.campaigns.forEach(c => {
          if (c.id != null) {
            map[String(c.id)] = {
              id: c.id,
              status: c.status || null,
              budget: c.budget != null ? c.budget : null,
              budgetId: c.budgetId || null
            };
          }
        });
        console.log('[ShoppingCampaigns] liveDataMap keys:', Object.keys(map));
        setLiveDataMap(map);
      } else {
        console.warn('[ShoppingCampaigns] campaigns-live failed:', { status: response.status, error: data.error });
      }
    } catch (e) {
      console.error('[ShoppingCampaigns] campaigns-live fetch error:', e);
    } finally {
      setIsLoadingLive(false);
    }
  }, [selectedBrand?.id]);

  // Fetch live data once on mount and whenever brand changes
  useEffect(() => {
    fetchLiveCampaignData();
  }, [fetchLiveCampaignData]);

  // Find and update the formatNumber function or any other function that formats currency values
  const formatNumber = (value, options = {}) => {
    if (value === undefined || value === null) return '0';
    
    const {
      prefix = '',
      suffix = '',
      minimumFractionDigits = 2,
      maximumFractionDigits = 2,
      notation = 'standard'
    } = options;
    
    // Remove any existing currency symbol from the value if it's a string
    let numericValue = value;
    if (typeof value === 'string') {
      numericValue = value.replace(/[₺$€£¥]/g, '');
    }
    
    // Convert to number
    numericValue = parseFloat(numericValue);
    
    // Format the number
    return `${prefix}${numericValue.toLocaleString(undefined, {
      minimumFractionDigits,
      maximumFractionDigits,
      notation
    })}${suffix}`;
  };

  // Get summary metrics - SADECE shopping kampanyaları varsa toplam metrikleri göster
  const summaryMetrics = shoppingCampaignsData?.totalMetrics || { 
    cpc: 0, 
    conversionRate: 0, 
    cost: 0, 
    roas: 0, 
    revenue: 0,
    impressions: 0,
    clicks: 0,
    transactions: 0
  };

  // Derive GA4 and session metrics if present in totalMetrics; otherwise aggregate from campaigns
  const campaignsToSort = shoppingCampaignsData?.campaigns || [];
  const summaryGa4Revenue = shoppingCampaignsData?.totalMetrics?.ga4Revenue ?? campaignsToSort.reduce((t, c) => t + (c.ga4Revenue || c.ga4_revenue || 0), 0);
  const summaryGa4Transactions = shoppingCampaignsData?.totalMetrics?.ga4Transaction ?? campaignsToSort.reduce((t, c) => t + (c.ga4Transaction || c.ga4_transaction || 0), 0);
  const summarySessions = shoppingCampaignsData?.totalMetrics?.sessions ?? campaignsToSort.reduce((t, c) => t + (c.sessions || 0), 0);
  const summaryCps = summarySessions > 0 ? (summaryMetrics.cost || 0) / summarySessions : 0;

  // useEffect içinde, sayfa yüklendiğinde varsayılan olarak budget'a göre sırala
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Shopping campaigns data'sını çek (view_google_ads_campaigntype_shopping'ten)
        await fetchShoppingCampaigns();
        
        // Shopping campaigns sadece kendi endpoint'ini kullanır
        
        // Varsayılan olarak budget'a göre azalan sıralama yap
        setSortField('budget');
        setSortDirection('desc');
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange]);

  // Kampanyaları sırala - shopping campaigns data'sını kullan
  const sortedCampaigns = [...campaignsToSort].sort((a, b) => {
    // Synthetic combined revenue (GA4 + Adjust)
    if (sortField === 'totalCombinedRevenue') {
      const aValue = (parseFloat(a.ga4Revenue || a.ga4_revenue) || 0) + (parseFloat(a.adjustAllRevenue || a.adjust_all_revenue) || 0);
      const bValue = (parseFloat(b.ga4Revenue || b.ga4_revenue) || 0) + (parseFloat(b.adjustAllRevenue || b.adjust_all_revenue) || 0);
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    // Sayısal değerler için sıralama
    if ([
      'spend',
      'budget',
      'impressions',
      'clicks',
      'ctr',
      'cpc',
      'conversions',
      'conversionRate',
      'costPerConversion',
      'conversionValue',
      'roas',
      'adjustSessions',
      'adjustInstalls',
      'adjustPurchaseEvents',
      'adjustAllRevenue'
    ].includes(sortField)) {
      // Sayısal değerleri güvenli bir şekilde al
      const aValue = parseFloat(
        a[sortField] ??
        (sortField === 'adjustSessions' ? a.adjust_sessions : undefined) ??
        (sortField === 'adjustInstalls' ? a.adjust_installs : undefined) ??
        (sortField === 'adjustPurchaseEvents' ? a.adjust_purchase_events : undefined) ??
        (sortField === 'adjustAllRevenue' ? a.adjust_all_revenue : undefined) ??
        0
      ) || 0;
      const bValue = parseFloat(
        b[sortField] ??
        (sortField === 'adjustSessions' ? b.adjust_sessions : undefined) ??
        (sortField === 'adjustInstalls' ? b.adjust_installs : undefined) ??
        (sortField === 'adjustPurchaseEvents' ? b.adjust_purchase_events : undefined) ??
        (sortField === 'adjustAllRevenue' ? b.adjust_all_revenue : undefined) ??
        0
      ) || 0;
      
      return sortDirection === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    }
    
    // Metin değerleri için sıralama (kampanya adı, durum vb.)
    if (['name', 'status', 'text'].includes(sortField)) {
      const aValue = String(a[sortField] || '').toLowerCase();
      const bValue = String(b[sortField] || '').toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    }
    
    // Varsayılan olarak budget'a göre azalan sıralama
    return b.budget - a.budget;
  });

  // Adjust verisi var mı kontrol et
  const hasAdjust = useMemo(() => {
    return sortedCampaigns.some(c =>
      ((c.adjustSessions || c.adjust_sessions || 0) +
        (c.adjustInstalls || c.adjust_installs || 0) +
        (c.adjustPurchaseEvents || c.adjust_purchase_events || 0) +
        (c.adjustAllRevenue || c.adjust_all_revenue || 0)) > 0
    );
  }, [sortedCampaigns]);

  // Re-fetch when custom range changes
  useEffect(() => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      fetchShoppingCampaigns();
    }
  }, [customStartDate, customEndDate, dateRange]);

  // Ad Group'ları sırala
  const sortAdGroups = (adGroups, field, direction) => {
    if (!field) return adGroups;

    return [...adGroups].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        // Sayısal değerleri güvenli bir şekilde al
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;

        return direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Metin değerleri için sıralama (ad group adı, durum vb.)
      if (['name', 'status', 'text'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Varsayılan olarak budget'a göre azalan sıralama
      return b.budget - a.budget;
    });
  };

  // Click-outside handler ekleyin
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDateDropdownOpen) {
        setIsDateDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDateDropdownOpen]);

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff]/70">
      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDialog(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className={`px-6 pt-6 pb-4 flex items-start gap-4 ${
              confirmDialog.variant === 'pause' ? 'bg-orange-50/50' :
              confirmDialog.variant === 'enable' ? 'bg-green-50/50' :
              'bg-blue-50/50'
            }`}>
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                confirmDialog.variant === 'pause' ? 'bg-orange-100' :
                confirmDialog.variant === 'enable' ? 'bg-green-100' :
                'bg-blue-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${
                  confirmDialog.variant === 'pause' ? 'text-orange-600' :
                  confirmDialog.variant === 'enable' ? 'text-green-600' :
                  'text-blue-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{confirmDialog.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{confirmDialog.message}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50/80 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 ${
                  confirmDialog.variant === 'pause' ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-200' :
                  confirmDialog.variant === 'enable' ? 'bg-green-500 hover:bg-green-600 focus:ring-green-200' :
                  'bg-blue-500 hover:bg-blue-600 focus:ring-blue-200'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white px-5 py-3 rounded-2xl shadow-sm w-full gap-3 md:gap-0 min-w-0">
        <div className="flex items-center gap-2.5 w-full md:w-auto">
          {/* Google Ads logosu yerine shopping.svg ikonumuzu koyuyoruz */}
          <img
            src="/shopping.svg"
            alt="Shopping Campaigns"
            className="w-7 h-7 object-contain"
          />
          <h1 className="text-lg font-semibold text-gray-900">Shopping Campaigns</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-center md:justify-end w-full md:w-auto">
          {/* Account Switcher */}
          <AccountSwitcher platform="GOOGLE_ADS" />

          {/* Seçili tarih aralığı */}
          <div className="text-xs text-gray-500">
            {(() => {
              switch (dateRange) {
                case 'today':
                  return new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'yesterday':
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return yesterday.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'last7':
                  const last7 = new Date();
                  last7.setDate(last7.getDate() - 7);
                  return `${last7.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'last30':
                  const last30 = new Date();
                  last30.setDate(last30.getDate() - 30);
                  return `${last30.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'last90':
                  const last90 = new Date();
                  last90.setDate(last90.getDate() - 90);
                  return `${last90.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'thisMonth':
                  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                  return `${firstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'lastMonth':
                  const lastMonthFirstDay = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
                  const lastMonthLastDay = new Date(new Date().getFullYear(), new Date().getMonth(), 0);
                  return `${lastMonthFirstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${lastMonthLastDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'custom':
                  if (customStartDate && customEndDate) {
                    return `${customStartDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${customEndDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                  }
                  return 'Custom Range';
                default:
                  return '';
              }
            })()}
          </div>

          {/* Google Ads Account Filter */}
          {gaAccounts.length >= 2 && (
            <select
              value={gaAccountFilter}
              onChange={(e) => setGaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All Accounts</option>
              {gaAccounts.map(acc => (
                <option key={acc.customerId} value={acc.customerId}>
                  {acc.nickname || acc.accountName || acc.customerId}
                </option>
              ))}
            </select>
          )}

          {/* Unified date range filter (replaces legacy dropdown + modal) */}
          <DateRangeFilter
            value={dateRange}
            customStart={customStartDate}
            customEnd={customEndDate}
            onChange={(range, start, end) => {
              setDateRange(range);
              setCustomStartDate(start);
              setCustomEndDate(end);
            }}
            presets={['yesterday', 'last7', 'last30', 'last90', 'thisMonth', 'lastMonth', 'custom']}
          />

        </div>
      </div>

      <Ga4WebOnlyNote />

      {/* Performance Overview */}
      <div className="grid grid-cols-12 gap-6">
        {/* Chart */}
        <Card className="col-span-8 rounded-2xl overflow-hidden bg-white">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                {/* Başlık ve açıklamayı değiştir */}
                <h2 className="text-lg font-semibold text-gray-900">Daily Performance Trend</h2>
                <p className="text-sm text-gray-500 mt-1">Shopping campaign performance over time</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-[300px]">
              {dataLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="loading-logo-container">
                    <img 
                      src="/logoicon.png" 
                      alt="Glowytics Logo" 
                      className="loading-logo"
                    />
                  </div>
                </div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </div>
        </Card>

        {/* Key Metrics - SearchCampaigns'deki gibi 5 satır yapıyoruz */}
        <div className="col-span-4 grid grid-cols-1 gap-3">
            {/* Row 1: Revenue + GA4 Revenue */}
          <div className="grid grid-cols-2 gap-3">
            {/* Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Revenue</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  ₺{(!isNaN(summaryMetrics.revenue) && summaryMetrics.revenue ? Math.floor(summaryMetrics.revenue).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">{formatNumber(summaryMetrics.roas || 0)}x ROAS</p>
              </div>
            </div>
            {/* GA4 Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">GA4 Revenue</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{(!isNaN(summaryGa4Revenue) && summaryGa4Revenue ? Math.floor(summaryGa4Revenue).toLocaleString('tr-TR') : '0')}</h3>
                <p className="text-xs text-gray-500">{formatNumber(((summaryMetrics.cost || 0) > 0 ? (summaryGa4Revenue || 0) / (summaryMetrics.cost || 0) : 0))}x ROAS</p>
              </div>
            </div>
          </div>

            {/* Row 2: Cost + Conversions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Cost Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Cost</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  ₺{(!isNaN(summaryMetrics.cost) && summaryMetrics.cost ? Math.floor(summaryMetrics.cost).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Conversions Card - Combined format */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center mb-1">
                <p className="text-gray-500 text-xs">Conversions</p>
                <div className="flex items-center gap-1">
                  <img src="/google_ads-512.webp" alt="Google Ads" className="w-3 h-3" />
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 text-center">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{summaryMetrics.transactions || 0}</h3>
                  <p className="text-xs text-gray-500">Ads</p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{summaryGa4Transactions || 0}</h3>
                  <p className="text-xs text-gray-500">GA4</p>
                </div>
              </div>
            </div>
          </div>

            {/* Row 3: Clicks + Sessions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Clicks Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Clicks</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{formatNumber(summaryMetrics.clicks || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Sessions Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Sessions</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{formatNumber(summarySessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
          </div>

            {/* Row 4: CPC + CPS */}
          <div className="grid grid-cols-2 gap-3">
            {/* CPC Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CPC</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{formatNumber(parseFloat(summaryMetrics.cpc) || 0, { prefix: '₺', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* CPS Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CPS</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{(summaryCps || 0).toFixed(2)}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
          </div>

          {/* Row 5: CTR + Impressions */}
          <div className="grid grid-cols-2 gap-3">
            {/* CTR Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CTR</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{isNaN(parseFloat(summaryMetrics.ctr)) ? '0.00' : parseFloat(summaryMetrics.ctr).toFixed(2)}%</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Impressions Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Impressions</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{formatNumber(summaryMetrics.impressions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Campaign Performance Table with Ad Groups */}
      <Card className="rounded-2xl overflow-hidden bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              {/* Başlık ve açıklamayı değiştir */}
              <h2 className="text-lg font-semibold text-gray-900">Shopping Campaign Performance</h2>
              <p className="text-sm text-gray-500 mt-1">Detailed metrics for campaigns, product groups, and products</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {dataLoading ? (
            <div className="min-h-[400px] flex items-center justify-center">
              <div className="loading-logo-container">
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                />
              </div>
            </div>
          ) : campaignsToSort.length === 0 ? (
            <div className="flex justify-center items-center p-12">
              <div className="text-center">
                <p className="text-gray-600 mb-2">No campaign data available</p>
                <p className="text-sm text-gray-500">Connect your Google Ads account to see campaign performance</p>
              </div>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th
                    className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('name')}
                  >
                    Campaign / Product Group / Product
                    {sortField === 'name' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('budget')}
                  >
                    Budget
                    {sortField === 'budget' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('spend')}
                  >
                    Cost
                    {sortField === 'spend' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('impressions')}
                  >
                    Impr.
                    {sortField === 'impressions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('clicks')}
                  >
                    Clicks
                    {sortField === 'clicks' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200"
                  >
                    Sessions
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ctr')}
                  >
                    CTR
                    {sortField === 'ctr' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cpc')}
                  >
                    Avg. CPC
                    {sortField === 'cpc' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversions')}
                  >
                    Conv.
                    {sortField === 'conversions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200">
                    GA4 Conv.
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionRate')}
                  >
                    Conv. Rate
                    {sortField === 'conversionRate' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('costPerConversion')}
                  >
                    Cost/Conv.
                    {sortField === 'costPerConversion' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionValue')}
                  >
                    Revenue
                    {sortField === 'conversionValue' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200">
                    GA4 Rev.
                  </th>
                  {hasAdjust && (
                    <>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustSessions')}
                      >
                        Adj. Sessions
                        {sortField === 'adjustSessions' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustInstalls')}
                      >
                        Adj. Installs
                        {sortField === 'adjustInstalls' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustPurchaseEvents')}
                      >
                        Adj. Purchases
                        {sortField === 'adjustPurchaseEvents' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustAllRevenue')}
                      >
                        Adj. Revenue
                        {sortField === 'adjustAllRevenue' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50 bg-emerald-50/40"
                        onClick={() => handleSort('totalCombinedRevenue')}
                        title="GA4 Revenue + Adjust Revenue"
                      >
                        <div className="flex items-center justify-end gap-1">
                          <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3 inline-block" />
                          <span className="text-gray-400">+</span>
                          <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3 inline-block" />
                          <span>Total Rev.</span>
                        </div>
                        {sortField === 'totalCombinedRevenue' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    </>
                  )}
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('roas')}
                  >
                    ROAS
                    {sortField === 'roas' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedCampaigns.map((campaign) => {
                  const campaignKey = getCampaignKey(campaign);
                  const campaignName = getCampaignName(campaign);
                  const campaignStatus = getCampaignStatus(campaign);
                  const campaignSpend = getCampaignSpend(campaign);
                  const campaignBudget = getCampaignBudget(campaign);
                  const impr = getCampaignImpr(campaign);
                  const clicks = getCampaignClicks(campaign);
                  const conv = getCampaignConv(campaign);
                  const ga4Conv = getCampaignGa4Conv(campaign);
                  const revenue = getCampaignRevenue(campaign);
                  const ga4Revenue = getCampaignGa4Revenue(campaign);
                  const sessions = getCampaignSessions(campaign);
                  const ctrVal = impr > 0 ? (clicks / impr) * 100 : (isNaN(campaign.ctr) ? 0 : campaign.ctr);
                  const cpcVal = clicks > 0 ? campaignSpend / clicks : (isNaN(campaign.cpc) ? 0 : campaign.cpc);
                  const convRateVal = clicks > 0 ? (conv / clicks) * 100 : (isNaN(campaign.conversionRate) ? 0 : campaign.conversionRate);
                  const costPerConvVal = conv > 0 ? campaignSpend / conv : (isNaN(campaign.costPerConversion) ? 0 : campaign.costPerConversion);
                  const roasVal = campaignSpend > 0 ? revenue / campaignSpend : (isNaN(campaign.roas) ? 0 : campaign.roas);
                  return (
                  <React.Fragment key={campaignKey}>
                    {/* Campaign Row */}
                    <tr
                      className={`group/row hover:bg-blue-50/30 cursor-pointer transition-colors ${
                        expandedCampaigns.has(campaignKey) ? 'bg-blue-50/50' : 'bg-white'
                      }`}
                      onClick={(e) => toggleCampaign(campaignKey, e)}
                    >
                      <td className="py-2.5 px-3 border-r border-gray-200 relative">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-5 h-5">
                            {expandedCampaigns.has(campaignKey) ?
                              <ChevronDown className="w-4 h-4 text-blue-500" /> :
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            }
                          </div>
                          {/* Status dot + hover arrow + Campaign Name */}
                          {(() => {
                            const live = liveDataMap[String(campaignKey)];
                            const resolvedStatus = cleanStatus(live?.status || campaignStatus || '');
                            const liveId = live?.id || campaignKey;
                            return (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenStatusDropdown(openStatusDropdown === campaignKey ? null : campaignKey); }}
                                  disabled={updatingId === campaignKey}
                                  className="flex-shrink-0 -ml-1 flex items-center gap-0.5 px-1 py-1 rounded-md hover:bg-gray-100 transition-colors focus:outline-none group/dot"
                                  title={resolvedStatus === 'ENABLED' ? 'Enabled — click to change' : 'Paused — click to change'}
                                >
                                  {updatingId === campaignKey ? (
                                    <Loader2 className="w-2.5 h-2.5 animate-spin text-gray-500" />
                                  ) : (
                                    <div className={`w-2.5 h-2.5 rounded-full ${resolvedStatus === 'ENABLED' ? 'bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.15)]' : 'bg-gray-400 shadow-[0_0_0_3px_rgba(156,163,175,0.15)]'}`} />
                                  )}
                                  <ChevronDown className="w-3 h-3 text-gray-400 opacity-0 group-hover/row:opacity-100 transition-opacity" />
                                </button>
                                {openStatusDropdown === campaignKey && (
                                  <>
                                    <div className="fixed inset-0 z-10 cursor-default" onClick={() => setOpenStatusDropdown(null)} />
                                    <div className="absolute top-full left-8 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Enable Campaign',
                                            message: `Are you sure you want to enable campaign "${campaignName}"?`,
                                            variant: 'enable',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'ENABLED', campaignKey, resolvedStatus, campaignName);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'ENABLED' ? 'text-green-600 bg-green-50/50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-green-500" />
                                        <span>Enable</span>
                                        {resolvedStatus === 'ENABLED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Pause Campaign',
                                            message: `Are you sure you want to pause campaign "${campaignName}"?`,
                                            variant: 'pause',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'PAUSED', campaignKey, resolvedStatus, campaignName);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'PAUSED' ? 'text-gray-900 bg-gray-50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                                        <span>Pause</span>
                                        {resolvedStatus === 'PAUSED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </>
                            );
                          })()}
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-transparent flex items-center justify-center">
                              <img
                                src="/shopping.svg"
                                alt="Shopping Campaign"
                                className="w-4 h-4"
                              />
                            </div>
                          </div>
                          <p className="text-[13px] font-medium text-gray-900">{campaignName}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right border-r border-gray-200" onClick={e => e.stopPropagation()}>
                        {(() => {
                          const live = liveDataMap[String(campaignKey)];
                          const isEditing = editBudget?.id === campaignKey;
                          const displayBudget = live?.budget != null ? live.budget : campaignBudget;
                          const hasBudgetId = !!(live?.budgetId || campaign.budget_id);

                          if (isEditing) {
                            return (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="number"
                                  value={editBudget.value}
                                  onChange={(e) => setEditBudget({ ...editBudget, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveBudget(campaignKey, campaignName);
                                    if (e.key === 'Escape') setEditBudget(null);
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                  autoFocus
                                />
                                <button onClick={() => handleSaveBudget(campaignKey, campaignName)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditBudget(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div className="flex items-center justify-end gap-2 group">
                              <span className="text-sm text-gray-900 font-medium">
                                {displayBudget != null ? `₺${displayBudget.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : isLoadingLive ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 inline" /> : '—'}
                              </span>
                              {hasBudgetId && (
                                <button
                                  onClick={() => setEditBudget({ id: campaignKey, value: displayBudget || 0 })}
                                  disabled={updatingId === campaignKey}
                                  className="opacity-0 group-hover/row:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition-opacity"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(campaignSpend)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(impr || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(clicks || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(sessions || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(ctrVal) ? '0.00' : ctrVal.toFixed(2)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(cpcVal) ? '0.00' : cpcVal.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{conv || 0}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{ga4Conv || 0}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(convRateVal) ? '0.00' : convRateVal.toFixed(2)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(costPerConvVal) ? '0.00' : costPerConvVal.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{(revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{(ga4Revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                      {hasAdjust && (
                        <>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustSessions || campaign.adjust_sessions || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustInstalls || campaign.adjust_installs || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustPurchaseEvents || campaign.adjust_purchase_events || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{(campaign.adjustAllRevenue || campaign.adjust_all_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-semibold text-emerald-700 bg-emerald-50/30">₺{((ga4Revenue || 0) + (campaign.adjustAllRevenue || campaign.adjust_all_revenue || 0)).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                        </>
                      )}
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">{isNaN(roasVal) ? '0.00' : roasVal.toFixed(2)}x</td>
                    </tr>

                    {/* Ad Groups Section */}
                    {expandedCampaigns.has(campaign.id) && campaign.adGroups && (
                      <>
                        {sortAdGroups(campaign.adGroups, sortField, sortDirection).map((adGroup) => (
                          <React.Fragment key={adGroup.id}>
                            {/* Ad Group Row */}
                            <tr
                              className="hover:bg-blue-50/20 bg-blue-50/5"
                              onClick={(e) => toggleAdGroup(adGroup.id, e)}
                            >
                              <td className="py-2 px-3 border-r border-gray-200">
                                <div className="flex items-center gap-2 pl-[2.75rem]">
                                  <div className="flex items-center justify-center w-5 h-5">
                                    {expandedAdGroups.has(adGroup.id) ?
                                      <ChevronDown className="w-4 h-4 text-blue-500" /> :
                                      <ChevronRight className="w-4 h-4 text-gray-400" />
                                    }
                                  </div>
                                  <div className="w-5 h-5 flex items-center justify-center">
                                    <img
                                      src="/enableicon.svg"
                                      alt="Ad Group"
                                      className="w-3 h-3"
                                    />
                                  </div>
                                  <div>
                                    <p className="text-[13px] font-medium text-gray-800">{adGroup.name}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.spend)}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.impressions || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.clicks || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{isNaN(adGroup.ctr) ? '0.00' : adGroup.ctr.toFixed(2)}%</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{isNaN(adGroup.cpc) ? '0.00' : adGroup.cpc.toFixed(2)}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{adGroup.conversions || 0}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{isNaN(adGroup.conversionRate) ? '0.00' : adGroup.conversionRate.toFixed(2)}%</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{isNaN(adGroup.costPerConversion) ? '0.00' : adGroup.costPerConversion.toFixed(2)}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{(adGroup.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                              {hasAdjust && (
                                <>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                                </>
                              )}
                              <td className="text-right py-2 px-3 text-[13px] font-medium text-gray-800">{isNaN(adGroup.roas) ? '0.00' : adGroup.roas.toFixed(2)}x</td>
                            </tr>

                            {/* Keywords Section */}
                            {expandedAdGroups.has(adGroup.id) && adGroup.keywords && sortKeywords(adGroup.keywords, sortField, sortDirection).map((keyword) => (
                              <tr
                                key={keyword.id}
                                className="hover:bg-blue-50/10 bg-blue-50/2"
                              >
                                <td className="py-2 px-3 border-r border-gray-200">
                                  <div className="flex items-center gap-2 pl-[5.5rem]">
                                    <p className="text-[12px] font-medium text-gray-700">{keyword.text}</p>
                                    <div className="flex gap-1">
                                      {keyword.matchTypes && keyword.matchTypes.map((matchType, index) => (
                                        <span key={index} className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                          matchType.includes('EXACT') ? 'bg-blue-100 text-blue-700' :
                                          matchType.includes('PHRASE') ? 'bg-green-100 text-green-700' :
                                          matchType.includes('BROAD') ? 'bg-purple-100 text-purple-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                          {matchType.replace('KeywordMatchType.', '').replace('KEYWORD_MATCH_TYPE_', '')}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-400">—</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">₺{formatNumber(keyword.spend)}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{(keyword.impressions || 0).toLocaleString()}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{(keyword.clicks || 0).toLocaleString()}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  {keyword.impressions > 0 ? ((keyword.clicks / keyword.impressions) * 100).toFixed(2) : '0.00'}%
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  ₺{formatNumber(keyword.clicks > 0 ? formatNumber((keyword.spend / keyword.clicks).toFixed(2)) : '0.00')}
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{keyword.conversions || 0}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  {keyword.clicks > 0 ? ((keyword.conversions / keyword.clicks) * 100).toFixed(2) : '0.00'}%
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  ₺{formatNumber(keyword.conversions > 0 ? formatNumber((keyword.spend / keyword.conversions).toFixed(2)) : '0.00')}
                                </td>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                    ₺{(keyword.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                  {hasAdjust && (
                                    <>
                                      <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">—</td>
                                      <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">—</td>
                                      <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">—</td>
                                      <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">—</td>
                                    </>
                                  )}
                                  <td className="text-right py-2 px-3 text-[12px] font-medium text-gray-700">
                                    {formatNumber(keyword.spend > 0 ? formatNumber((keyword.conversionValue / keyword.spend).toFixed(2)) : '0.00')}x
                                  </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                )})}
              </tbody>
              {/* Toplam Satırı - Tüm kampanyaların toplamı */}
              {/* Toplam Satırı - Sadece kampanya varsa göster */}
              {campaignsToSort.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-100 border-t-2 border-gray-300">
                    <td className="py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      Total
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200">
                      {/* Budget sütunu için toplam yok */}
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      ₺{formatNumber(campaignsToSort.reduce((total, campaign) => total + (campaign.spend || campaign.cost || 0), 0))}
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      {campaignsToSort.reduce((total, campaign) => total + (campaign.impressions || 0), 0).toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{campaignsToSort.reduce((total, campaign) => total + (campaign.clicks || campaign.googleAdsClicks || campaign.google_ads_clicks || 0), 0).toLocaleString()}</td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{campaignsToSort.reduce((total, campaign) => total + (campaign.sessions || 0), 0).toLocaleString()}</td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      {(() => {
                        const totalImpressions = campaignsToSort.reduce((total, campaign) => total + (campaign.impressions || campaign.googleAdsImpression || campaign.google_ads_impression || 0), 0);
                        const totalClicks = campaignsToSort.reduce((total, campaign) => total + (campaign.clicks || campaign.googleAdsClicks || campaign.google_ads_clicks || 0), 0);
                        return totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
                      })()}%
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      ₺{(() => {
                        const totalClicks = campaignsToSort.reduce((total, campaign) => total + (campaign.clicks || campaign.googleAdsClicks || campaign.google_ads_clicks || 0), 0);
                        const totalSpend = campaignsToSort.reduce((total, campaign) => total + (campaign.spend || campaign.cost || 0), 0);
                        return totalClicks > 0 ? (totalSpend / totalClicks).toFixed(2) : '0.00';
                      })()}
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      {campaignsToSort.reduce((total, campaign) => total + (campaign.conversions || campaign.transactions || campaign.googleAdsTransaction || campaign.google_ads_transaction || 0), 0).toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{campaignsToSort.reduce((total, campaign) => total + (campaign.ga4Transaction || campaign.ga4_transaction || 0), 0).toLocaleString()}</td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      {(() => {
                        const totalClicks = campaignsToSort.reduce((total, campaign) => total + (campaign.clicks || campaign.googleAdsClicks || campaign.google_ads_clicks || 0), 0);
                        const totalConversions = campaignsToSort.reduce((total, campaign) => total + (campaign.conversions || campaign.transactions || campaign.googleAdsTransaction || campaign.google_ads_transaction || 0), 0);
                        return totalClicks > 0 ? ((totalConversions / totalClicks) * 100).toFixed(2) : '0.00';
                      })()}%
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      ₺{(() => {
                        const totalConversions = campaignsToSort.reduce((total, campaign) => total + (campaign.conversions || campaign.transactions || campaign.googleAdsTransaction || campaign.google_ads_transaction || 0), 0);
                        const totalSpend = campaignsToSort.reduce((total, campaign) => total + (campaign.spend || campaign.cost || 0), 0);
                        return totalConversions > 0 ? (totalSpend / totalConversions).toFixed(2) : '0.00';
                      })()}
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      ₺{formatNumber(campaignsToSort.reduce((total, campaign) => total + (campaign.conversionValue || campaign.revenue || campaign.googleAdsRevenue || campaign.google_ads_revenue || 0), 0))}
                    </td>
                    <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{formatNumber(campaignsToSort.reduce((total, campaign) => total + (campaign.ga4Revenue || campaign.ga4_revenue || 0), 0))}</td>
                    {hasAdjust && (
                      <>
                        <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                          {campaignsToSort.reduce((total, campaign) => total + (campaign.adjustSessions || campaign.adjust_sessions || 0), 0).toLocaleString()}
                        </td>
                        <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                          {campaignsToSort.reduce((total, campaign) => total + (campaign.adjustInstalls || campaign.adjust_installs || 0), 0).toLocaleString()}
                        </td>
                        <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                          {campaignsToSort.reduce((total, campaign) => total + (campaign.adjustPurchaseEvents || campaign.adjust_purchase_events || 0), 0).toLocaleString()}
                        </td>
                        <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                          ₺{formatNumber(campaignsToSort.reduce((total, campaign) => total + (campaign.adjustAllRevenue || campaign.adjust_all_revenue || 0), 0), { precision: 0 })}
                        </td>
                        <td className="text-right py-3 px-3 border-r border-gray-200 font-semibold text-[13px] text-emerald-700 bg-emerald-50/30">
                          ₺{formatNumber(campaignsToSort.reduce((total, campaign) => total + (campaign.ga4Revenue || campaign.ga4_revenue || 0) + (campaign.adjustAllRevenue || campaign.adjust_all_revenue || 0), 0), { precision: 0 })}
                        </td>
                      </>
                    )}
                    <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">
                      {(() => {
                        const totalSpend = campaignsToSort.reduce((total, campaign) => total + (campaign.spend || campaign.cost || 0), 0);
                        const totalConversionValue = campaignsToSort.reduce((total, campaign) => total + (campaign.conversionValue || campaign.revenue || campaign.googleAdsRevenue || campaign.google_ads_revenue || 0), 0);
                        return totalSpend > 0 ? (totalConversionValue / totalSpend).toFixed(2) : '0.00';
                      })()}x
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </Card>

      {/* Custom date modal removed — <DateRangeFilter /> owns the calendar popover now */}

      {/* Change History Slide-over */}
      {showChangeHistory && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowChangeHistory(false)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <History className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Change History</h2>
                  <p className="text-xs text-gray-500">{changeHistoryTotal} total changes</p>
                </div>
              </div>
              <button onClick={() => setShowChangeHistory(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-3 border-b border-gray-50 flex gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'status_change', label: 'Status' },
                { key: 'budget_change', label: 'Budget' }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setChangeHistoryFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    changeHistoryFilter === f.key
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {changeHistoryLoading && changeHistoryData.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : changeHistoryData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No changes recorded yet</p>
                  <p className="text-xs text-gray-400 mt-1">Changes to campaigns will appear here</p>
                </div>
              ) : (
                <>
                  {changeHistoryData.map((item, idx) => {
                    const isStatus = item.action === 'status_change';
                    const isBudget = item.action === 'budget_change';
                    const timeAgo = (() => {
                      const diff = Date.now() - new Date(item.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins}m ago`;
                      const hours = Math.floor(mins / 60);
                      if (hours < 24) return `${hours}h ago`;
                      const days = Math.floor(hours / 24);
                      if (days < 7) return `${days}d ago`;
                      return new Date(item.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    })();
                    const formatValue = (val, field) => {
                      if (!val) return '—';
                      if (field === 'amount_micros' || field === 'daily_budget') {
                        const num = parseFloat(val) / 1000000;
                        return `₺${num.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
                      }
                      return val;
                    };
                    return (
                      <div key={item.id || idx} className={`p-3.5 rounded-xl border transition-colors ${item.success ? 'border-gray-100 bg-white hover:bg-gray-50/50' : 'border-red-100 bg-red-50/30'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${
                              !item.success ? 'bg-red-100' : isStatus ? 'bg-amber-100' : isBudget ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              {!item.success ? <XCircle className="w-4 h-4 text-red-500" /> :
                               isStatus ? <Activity className="w-4 h-4 text-amber-600" /> :
                               <DollarSign className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
                                  isStatus ? 'bg-amber-100 text-amber-700' : isBudget ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {isStatus ? 'Status' : isBudget ? 'Budget' : item.action}
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  item.platform === 'GOOGLE_ADS' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {item.platform === 'GOOGLE_ADS' ? 'Google' : 'Meta'}
                                </span>
                              </div>
                              {item.entity_name && (
                                <p className="text-sm font-medium text-gray-800 mt-1 truncate">{item.entity_name}</p>
                              )}
                              <div className="flex items-center gap-1.5 mt-1 text-xs">
                                <span className="text-gray-400">{formatValue(item.old_value, item.field_name)}</span>
                                <span className="text-gray-300">→</span>
                                <span className={`font-medium ${item.success ? 'text-gray-700' : 'text-red-600'}`}>
                                  {formatValue(item.new_value, item.field_name)}
                                </span>
                              </div>
                              {!item.success && item.error_message && (
                                <p className="text-xs text-red-500 mt-1 truncate">{item.error_message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1 text-[11px] text-gray-400">
                              <Clock className="w-3 h-3" />
                              {timeAgo}
                            </div>
                            {item.user_email && (
                              <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[120px]">{item.user_name || item.user_email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {changeHistoryData.length < changeHistoryTotal && (
                    <button
                      onClick={() => fetchChangeHistory(changeHistoryData.length, true)}
                      disabled={changeHistoryLoading}
                      className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {changeHistoryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Load More ({changeHistoryTotal - changeHistoryData.length} remaining)
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}